/** 공개 문서가 바뀔 때 알리는 장치 · 웹 (`W-90` · `P-04b` · `W-70` 다시 여는 줄. PRD `7.5.17`, 법무 `4.19`(4) · `L-C38`-5).
 *
 * ## 장치는 범용이다
 *
 * 문서 이름 · 시행일 · 바뀌는 곳 · 전문 경로를 **값으로**(`PolicyAmendment`) 받는다. 이 파일에 문서별 글자를
 * 적지 않는다. 값의 정본은 `@makit/shared` 의 `PENDING_POLICY_AMENDMENT` 이고, 앱(`A-60`)도 같은 값과 같은 칸
 * 이름을 쓴다.
 *
 * ⛔ **`동의` 버튼을 두지 않는다**(요건 ⑦). 버튼은 `확인` 하나다.
 * ⛔ 셀러에게 이익을 단정하는 문장(`더 안전해집니다` 류)을 더하지 않는다.
 * ⚠️ 웹에는 탈퇴 자리가 아직 없어(`W-80` 캡션) `원하지 않으시면` 칸은 창구 문장만 둔다. 없는 계단을 그리지 않는다.
 */

import { CONTACT_EMAIL, amendmentMonthDay, type PolicyAmendment } from "@makit/shared";
import { Link } from "react-router-dom";

import { Button } from "../../components/primitives";
import { SettingRow } from "../../components/settings";

/** 제목 꼴 `{문서 이름}이 바뀝니다`. 문서 이름 셋이 모두 받침으로 끝나 `이` 를 쓴다. */
export function amendmentTitle(amendment: PolicyAmendment): string {
  return `${amendment.docName}이 바뀝니다`;
}

/** 시행일까지 다시 여는 줄의 글자(`›` 는 줄이 붙인다). */
export function amendmentReopenLabel(amendment: PolicyAmendment): string {
  return `바뀌는 ${amendment.docName} · ${amendmentMonthDay(amendment)}부터`;
}

/** `W-90` 본문. 넓은 화면이라 바뀌는 곳을 왼쪽에, 전문과 창구를 오른쪽에 편다(목업). */
export function AmendmentNotice({ amendment, onConfirm }: { amendment: PolicyAmendment; onConfirm: () => void }) {
  return (
    <div className="review" style={{ marginTop: 16 }}>
      <div>
        <div className="setgrp">
          <h4>바뀌는 곳</h4>
          {amendment.changes.map((change, i) => (
            <div key={change.article}>
              <span className="setlink" style={i > 0 ? { marginTop: 8 } : undefined}>
                <span>
                  <b>{change.article}</b>
                </span>
                <span className="v" />
              </span>
              <p className="hint">지금: {change.before}</p>
              <p className="hint">바뀐 뒤: {change.after}</p>
            </div>
          ))}
          {amendment.footnote ? (
            <p className="hint" style={{ marginTop: 8 }}>
              {amendment.footnote}
            </p>
          ) : null}
        </div>
        <div className="actions">
          <Button label="확인" small onClick={onConfirm} />
          <span className="why">시행일까지 마이페이지에서 다시 보실 수 있습니다.</span>
        </div>
      </div>
      <div className="panel">
        <section>
          <h3>전문</h3>
          <Link className="setlink clickable" to={amendment.nextPath}>
            <span>바뀐 뒤 전문</span>
            <span className="chev" aria-hidden="true">
              ›
            </span>
          </Link>
          <Link className="setlink clickable" to={amendment.currentPath}>
            <span>지금 전문</span>
            <span className="chev" aria-hidden="true">
              ›
            </span>
          </Link>
        </section>
        <section>
          <h3>원하지 않으시면</h3>
          <p className="hint">{CONTACT_EMAIL}로 알려 주세요. 시행일 전에 탈퇴하실 수도 있습니다.</p>
        </section>
      </div>
    </div>
  );
}

/** `P-04b`: 로그인 전 공개 문서 화면 머리 아래 블록. `{시행일}부터 바뀝니다.` + 바뀌는 곳 한 줄 + `바뀐 뒤 전문 ›`.
 *  로그인 없이 누구나 본다. 앱 · 웹 고지를 못 본 셀러(옛 앱 빌드 포함)도 여기서 본다(요건 ⑥). */
export function AmendmentBlock({ amendment }: { amendment: PolicyAmendment }) {
  return (
    <div className="limit" style={{ margin: "4px 0 12px" }}>
      <span className="ico" aria-hidden="true">
        i
      </span>
      <span className="body">
        <span className="l1">
          <span className="mono">{amendment.effectiveDate}</span>부터 바뀝니다.
        </span>
        <span className="l2">
          {amendment.summary} <Link to={amendment.nextPath}>바뀐 뒤 전문 ›</Link>
        </span>
      </span>
    </div>
  );
}

/** `W-70` 맨 위의 다시 여는 줄. */
export function AmendmentReopenRow({ amendment, onOpen }: { amendment: PolicyAmendment; onOpen: () => void }) {
  return <SettingRow label={amendmentReopenLabel(amendment)} action="page" onClick={onOpen} />;
}
